// «Бюджет и режим агента» (вкладка «Автономия и деньги»): единственный денежный лимит — дневной бюджет на рекламу,
// целевая маржа (от неё — целевой CPL офферов), режим агента и исключения по действиям, выключатель записи
// в Meta и белые списки назначений.
// Меняет только владелец; каждое изменение платформа пишет в журнал решений.
import { Button, Card, Chip, Meter, pct, toast, usd, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Lock, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';

type Mode = 'analytics_only' | 'propose' | 'autonomous_limited' | 'full';

interface IdName {
  id: string;
  name: string;
}

export interface PolicyView {
  autonomy: { mode: Mode; overrides: Record<string, Mode> };
  dailyBudgetUsd: number | null;
  allocatedUsd: number;
  unknownCurrency: string | null;
  write: { enabled: boolean; token: boolean };
  allow: { pages: IdName[]; pixels: IdName[]; domains: string[] };
  known: { pages: string[]; pixels: string[] };
  domains: string[];
  accounts: Array<{ id: string; name: string | null; enabled: boolean; currency: string; status: number }>;
  locks: Array<{
    objectId: string;
    objectType: string;
    name: string | null;
    rule: string;
    lockedAt: string;
    agentUntil: string | null;
  }>;
  writesLastHour: number;
  writesPerHour: number;
  actions: Array<{ type: string; label: string; domain: string }>;
}

const MODES: Array<{ value: Mode; title: string; text: string }> = [
  {
    value: 'analytics_only',
    title: 'Только анализ',
    text: 'Агент советует «в тень», платформа ничего не меняет. Вы отмечаете, согласны ли.',
  },
  {
    value: 'propose',
    title: 'Предлагает → вы одобряете',
    text: 'Каждое действие ждёт вашего «Одобрить» в «Решениях» или в Telegram.',
  },
  {
    value: 'autonomous_limited',
    title: 'Автономно в лимитах',
    text: 'Агент действует сам в пределах дневного бюджета, вы видите всё в журнале и можете откатить.',
  },
  {
    value: 'full',
    title: 'Полная автономия',
    text: 'Всё сам, без ваших одобрений, как Claude Code: команды, реклама, батчи, лендинги — и после интернета. Жёстко держатся дневной бюджет и потолок генераций; с вами — только доступы к кабинетам и бизнесу.',
  },
];
const MODE_SHORT: Record<Mode, string> = {
  analytics_only: 'только анализ',
  propose: 'с одобрения',
  autonomous_limited: 'сам в лимитах',
  full: 'полностью сам',
};

const RULE_RU: Record<string, string> = {
  spend_no_leads: 'расход без лидов',
  cpl_spike: 'всплеск CPL',
  silence: 'тишина',
};

/** Число долларов: сохраняется при выходе из поля или Enter; пусто — «не задан». */
function BudgetInput({ value, onSave }: { value: number | null; onSave: (n: number | null) => void }) {
  const [v, setV] = useState(value === null ? '' : String(value));
  useEffect(() => setV(value === null ? '' : String(value)), [value]);
  const commit = () => {
    const t = v.trim().replace(',', '.').replace(/\s/g, '');
    if (!t) return value !== null && onSave(null);
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0) return setV(value === null ? '' : String(value));
    if (n !== value) onSave(Math.round(n * 100) / 100);
  };
  return (
    <div className="auto-budget-input">
      <span aria-hidden="true">$</span>
      <input
        className="input tnum"
        inputMode="decimal"
        aria-label="Дневной бюджет на рекламу, долларов"
        placeholder="не задан"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      <span className="muted">в день</span>
    </div>
  );
}

/** Маржа, целые проценты 0–90: сохраняется при выходе из поля или Enter; пусто — по умолчанию. */
function MarginInput({
  value,
  fallback,
  onSave,
}: {
  value: number;
  fallback: number;
  onSave: (n: number | null) => void;
}) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => {
    const t = v.trim().replace(',', '.').replace('%', '');
    if (!t) return value !== fallback ? onSave(null) : setV(String(value));
    const n = Math.round(Number(t));
    if (!Number.isFinite(n) || n < 0 || n > 90) {
      toast('Маржа — от 0 до 90%');
      return setV(String(value));
    }
    if (n !== value) onSave(n);
  };
  return (
    <div className="auto-budget-input">
      <input
        className="input tnum"
        inputMode="numeric"
        aria-label="Целевая маржа, процентов"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      <span className="muted">%</span>
    </div>
  );
}

/** Список id + название (страницы, пиксели) с добавлением и удалением. */
function IdList({
  title,
  hint,
  items,
  known,
  onChange,
}: {
  title: string;
  hint: string;
  items: IdName[];
  known: string[];
  onChange: (next: IdName[]) => void;
}) {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const extra = known.filter((k) => !items.some((i) => i.id === k));
  const add = () => {
    const clean = id.trim();
    if (!/^\d{5,25}$/.test(clean)) return toast('id — только цифры, как в Meta');
    if (items.some((i) => i.id === clean)) return toast('Уже в списке');
    onChange([...items, { id: clean, name: name.trim() }]);
    setId('');
    setName('');
  };
  return (
    <div className="auto-list">
      <b>{title}</b>
      <p className="muted">{hint}</p>
      <ul>
        {items.map((i) => (
          <li key={i.id}>
            <span className="mono">{i.id}</span>
            {i.name && <span className="auto-name">{i.name}</span>}
            <button
              type="button"
              className="auto-x"
              aria-label={`Убрать ${i.name || i.id}`}
              onClick={() => onChange(items.filter((x) => x.id !== i.id))}
            >
              <X />
            </button>
          </li>
        ))}
        {extra.map((k) => (
          <li key={k} className="auto-known">
            <span className="mono">{k}</span>
            <Chip>из вашей рекламы</Chip>
          </li>
        ))}
        {!items.length && !extra.length && <li className="muted">Пока пусто</li>}
      </ul>
      <div className="auto-add">
        <input
          className="input mono"
          placeholder="id"
          inputMode="numeric"
          aria-label={`${title}: id`}
          value={id}
          onChange={(e) => setId(e.target.value)}
        />
        <input
          className="input"
          placeholder="название (для себя)"
          aria-label={`${title}: название`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
        />
        <Button size="sm" icon={<Plus />} onClick={add}>
          Добавить
        </Button>
      </div>
    </div>
  );
}

function DomainList({
  own,
  items,
  onChange,
}: {
  own: string[];
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const [v, setV] = useState('');
  const add = () => {
    const d = v
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '');
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(d)) return toast('Домен вида shop.example.uz');
    if (items.includes(d) || own.includes(d)) return toast('Уже в списке');
    onChange([...items, d]);
    setV('');
  };
  return (
    <div className="auto-list">
      <b>Домены лендингов</b>
      <p className="muted">
        Свои домены из «Лендингов» разрешены всегда. Добавьте чужие, только если сами ведёте на них рекламу.
      </p>
      <ul>
        {own.map((d) => (
          <li key={d} className="auto-known">
            <span className="mono">{d}</span>
            <Chip>ваш лендинг</Chip>
          </li>
        ))}
        {items.map((d) => (
          <li key={d}>
            <span className="mono">{d}</span>
            <button
              type="button"
              className="auto-x"
              aria-label={`Убрать ${d}`}
              onClick={() => onChange(items.filter((x) => x !== d))}
            >
              <X />
            </button>
          </li>
        ))}
        {!own.length && !items.length && <li className="muted">Пока пусто</li>}
      </ul>
      <div className="auto-add auto-add-2">
        <input
          className="input mono"
          placeholder="shop.example.uz"
          aria-label="Домен лендинга"
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
        />
        <Button size="sm" icon={<Plus />} onClick={add}>
          Добавить
        </Button>
      </div>
    </div>
  );
}

export function AutonomyCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['policy'], queryFn: () => api<PolicyView>('/api/agent/policy') });
  const margin = useQuery({
    queryKey: ['margin'],
    queryFn: () => api<{ marginPct: number; defaultPct: number }>('/api/finance/margin'),
  });
  const trust = useQuery({
    queryKey: ['trust'],
    queryFn: () => api<Array<{ domain: string; agree: number; disagree: number }>>('/api/agent/trust'),
  });
  const v = q.data;

  const put = async (path: string, body: unknown, patch: Partial<PolicyView>, ok: string) => {
    qc.setQueryData<PolicyView>(['policy'], (old) => (old ? { ...old, ...patch } : old));
    try {
      await api(`/api/agent/policy/${path}`, body, 'PUT');
      toast(ok);
    } finally {
      // Бюджет распределения и списки платформа пересчитывает сама
      void qc.invalidateQueries({ queryKey: ['policy'] });
    }
  };
  const guarded = (fn: () => Promise<unknown>) =>
    void fn().catch((e) => {
      toast((e as Error).message);
    });
  /** Маржа: целевой CPL офферов в «Рекламе», «Офферах» и у агента пересчитается сразу. */
  const saveMargin = async (pct: number | null) => {
    try {
      const r = await api<{ marginPct: number; defaultPct: number }>(
        '/api/finance/margin',
        { marginPct: pct },
        'PUT',
      );
      qc.setQueryData(['margin'], r);
      toast(`Целевая маржа: ${r.marginPct}%`);
    } finally {
      void qc.invalidateQueries({ queryKey: ['margin'] });
      void qc.invalidateQueries({ queryKey: ['offers'] });
      void qc.invalidateQueries({ queryKey: ['ads'] });
    }
  };

  if (q.isPending)
    return (
      <Card title="Бюджет и режим агента">
        <div className="flex flex-col gap-3">
          <div className="skeleton" style={{ height: 44 }} />
          <div className="skeleton" style={{ height: 120 }} />
        </div>
      </Card>
    );
  if (q.isError || !v)
    return (
      <Card title="Бюджет и режим агента">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="muted">Не удалось загрузить настройки. {(q.error as Error | null)?.message}</span>
          <Button size="sm" onClick={() => q.refetch()}>
            Повторить
          </Button>
        </div>
      </Card>
    );

  const agree = (trust.data ?? []).reduce((a, r) => a + r.agree, 0);
  const disagree = (trust.data ?? []).reduce((a, r) => a + r.disagree, 0);
  const budget = v.dailyBudgetUsd;
  const share = budget ? v.allocatedUsd / budget : 0;
  const overrides = Object.entries(v.autonomy.overrides);

  return (
    <Card
      title="Бюджет и режим агента"
      action={
        v.write.enabled && v.write.token ? (
          <Chip tone="success" dot>
            Запись в Meta включена
          </Chip>
        ) : (
          <Chip dot>Только советы</Chip>
        )
      }
    >
      <section className="auto-sec">
        <div className="auto-budget">
          <div className="min-w-0">
            <b>Дневной бюджет на рекламу</b>
            <p className="muted">
              Единственный денежный лимит: сумма дневных бюджетов всех кампаний и адсетов не превысит его, кто
              бы их ни менял. Меняете только вы.
            </p>
          </div>
          <BudgetInput
            value={budget}
            onSave={(n) =>
              guarded(() =>
                put(
                  'budget',
                  { dailyUsd: n },
                  { dailyBudgetUsd: n },
                  n === null ? 'Дневной бюджет снят' : `Дневной бюджет: ${usd(n)}`,
                ),
              )
            }
          />
        </div>
        <div className="auto-alloc">
          <div className="flex justify-between gap-3" style={{ fontSize: 'var(--fs-sm)' }}>
            <span>Распределено сейчас</span>
            <span className="muted tnum">
              {usd(v.allocatedUsd)}
              {budget ? ` из ${usd(budget)} · ${pct(share * 100)}` : ''}
            </span>
          </div>
          {budget ? (
            <Meter
              value={Math.min(1, share)}
              label="Распределено из дневного бюджета"
              tone={share >= 1 ? 'danger' : share >= 0.85 ? 'warn' : undefined}
            />
          ) : (
            <p className="auto-warn">Бюджет не задан — агент не может увеличивать расход, только снижать.</p>
          )}
          {v.unknownCurrency && (
            <p className="auto-warn">
              Кабинет {v.unknownCurrency} не в долларах — в нём платформа не даст увеличивать расход.
            </p>
          )}
        </div>
        <div className="auto-budget auto-margin" id="margin">
          <div className="min-w-0">
            <b>Целевая маржа</b>
            <p className="muted">
              Окупаемый CPL оффера — сколько можно платить за лид в ноль: прогнозный апрув × выплата. Целевой
              — ниже на эту маржу. По ним «Реклама» красит CPL, а агент решает, что масштабировать и что
              останавливать.
            </p>
          </div>
          {margin.data ? (
            <MarginInput
              value={margin.data.marginPct}
              fallback={margin.data.defaultPct}
              onSave={(n) => guarded(() => saveMargin(n))}
            />
          ) : (
            <div className="skeleton" style={{ height: 38, width: 96 }} />
          )}
        </div>
      </section>

      <section className="auto-sec">
        <b className="auto-h">Режим агента</b>
        <div className="auto-modes" role="radiogroup" aria-label="Режим агента">
          {MODES.map((m) => (
            <label key={m.value} className={`auto-mode${v.autonomy.mode === m.value ? ' on' : ''}`}>
              <input
                type="radio"
                name="autonomy-mode"
                checked={v.autonomy.mode === m.value}
                onChange={() =>
                  guarded(() =>
                    put(
                      'autonomy',
                      { mode: m.value, overrides: v.autonomy.overrides },
                      { autonomy: { ...v.autonomy, mode: m.value } },
                      `Режим: ${m.title.toLowerCase()}`,
                    ),
                  )
                }
              />
              <span>
                <b>{m.title}</b>
                <span className="muted">{m.text}</span>
              </span>
            </label>
          ))}
        </div>
        {overrides.length > 0 && (
          <p className="auto-overrides">
            {overrides.map(([type, m]) => (
              <Chip key={type} tone="accent">
                {v.actions.find((a) => a.type === type)?.label ?? type}: {MODE_SHORT[m]}
              </Chip>
            ))}
          </p>
        )}
        <p className="muted auto-trust">
          {agree + disagree > 0
            ? `За 30 дней вы согласились с ${agree} из ${agree + disagree} предложений агента (${pct((agree / (agree + disagree)) * 100)}) — по этой цифре удобно решать, когда давать ему больше свободы.`
            : 'Когда агент начнёт предлагать, здесь появится, как часто вы с ним соглашаетесь.'}
        </p>
        <details className="auto-details">
          <summary>Отдельно по действиям</summary>
          <p className="muted">
            Например, «Остановить» — полностью сам, а «Запустить новое» — только с одобрения.
          </p>
          <ul className="auto-over">
            {v.actions.map((a) => {
              const own = v.autonomy.overrides[a.type];
              return (
                <li key={a.type}>
                  <span>{a.label}</span>
                  <select
                    className="input"
                    aria-label={`Режим: ${a.label}`}
                    value={own ?? ''}
                    onChange={(e) => {
                      const next = { ...v.autonomy.overrides };
                      if (e.target.value) next[a.type] = e.target.value as Mode;
                      else delete next[a.type];
                      guarded(() =>
                        put(
                          'autonomy',
                          { mode: v.autonomy.mode, overrides: next },
                          { autonomy: { ...v.autonomy, overrides: next } },
                          `${a.label}: ${e.target.value ? MODE_SHORT[e.target.value as Mode] : 'как в режиме'}`,
                        ),
                      );
                    }}
                  >
                    <option value="">Как в режиме ({MODE_SHORT[v.autonomy.mode]})</option>
                    {MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.title}
                      </option>
                    ))}
                  </select>
                </li>
              );
            })}
          </ul>
        </details>
      </section>

      <section className="auto-sec">
        <div className="auto-write">
          <input
            id="meta-write"
            type="checkbox"
            checked={v.write.enabled}
            onChange={(e) => {
              const enabled = e.target.checked;
              guarded(() =>
                put(
                  'meta-write',
                  { enabled },
                  { write: { ...v.write, enabled } },
                  enabled ? 'Запись в Meta включена' : 'Запись в Meta выключена',
                ),
              );
            }}
          />
          <div className="min-w-0">
            <label htmlFor="meta-write">
              <b>Запись в Meta</b>
            </label>
            <p className="muted">
              {v.write.enabled
                ? 'Одобренное (или разрешённое режимом) платформа выполняет в кабинете — пауза, бюджет, копия адсета, запуск, — заново сверив лимиты. Выключите — всё снова станет советами.'
                : 'Включите, когда будете готовы: одобренное (или разрешённое режимом) платформа выполнит в кабинете — пауза, бюджет, копия адсета, запуск. Пока выключено — всё остаётся советами, автостоп страховки ждёт.'}
            </p>
            {!v.write.token && (
              <p className="auto-warn">
                Токен Meta не подключён —{' '}
                <Link to="/settings" search={{ tab: 'integrations' }} hash="meta">
                  подключите его в «Интеграциях»
                </Link>
                .
              </p>
            )}
            <p className="muted" style={{ marginTop: 4 }}>
              Правок в Meta за последний час: <span className="tnum">{v.writesLastHour}</span> из{' '}
              <span className="tnum">{v.writesPerHour}</span> — больше агенту нельзя.
            </p>
          </div>
        </div>
      </section>

      <section className="auto-sec">
        <details className="auto-details">
          <summary>Куда агенту можно вести рекламу</summary>
          <p className="muted">
            Белые списки: страница с «инструкциями» из интернета не уведёт трафик на чужой сайт и не запустит
            рекламу от чужой страницы. Всё, что уже стоит в вашей рекламе, разрешено само.
          </p>
          <IdList
            title="Страницы Facebook"
            hint="От имени этих страниц агент может запускать объявления."
            items={v.allow.pages}
            known={v.known.pages}
            onChange={(pages) =>
              guarded(() =>
                put('allow', { ...v.allow, pages }, { allow: { ...v.allow, pages } }, 'Страницы сохранены'),
              )
            }
          />
          <IdList
            title="Пиксели"
            hint="На эти пиксели агент может оптимизировать адсеты."
            items={v.allow.pixels}
            known={v.known.pixels}
            onChange={(pixels) =>
              guarded(() =>
                put('allow', { ...v.allow, pixels }, { allow: { ...v.allow, pixels } }, 'Пиксели сохранены'),
              )
            }
          />
          <DomainList
            own={v.domains}
            items={v.allow.domains}
            onChange={(domains) =>
              guarded(() =>
                put('allow', { ...v.allow, domains }, { allow: { ...v.allow, domains } }, 'Домены сохранены'),
              )
            }
          />
        </details>
      </section>

      {v.locks.length > 0 && (
        <section className="auto-sec">
          <b className="auto-h">Остановлено страховкой</b>
          <ul className="auto-locks">
            {v.locks.map((l) => (
              <li key={l.objectId}>
                <Lock aria-hidden="true" />
                <div className="min-w-0">
                  <b>{l.name ?? l.objectId}</b>
                  <span>
                    {RULE_RU[l.rule] ?? l.rule} · {when(l.lockedAt)} ·{' '}
                    {l.agentUntil ? `агенту можно после ${when(l.agentUntil)}` : 'включаете только вы'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 'var(--fs-sm)' }}>
            Включить обратно — в «Решениях», у записи страховки.
          </p>
        </section>
      )}
    </Card>
  );
}
